import { fieldValuesFromUploadEntry, uploadEntryHasPreviewSamples } from '@/features/datasets/utils/filePreviewUtils';

/**
 * Multilabel classification labels-delimiter options and auto-detection.
 * Option set mirrors the legacy upload modal (DatasetUploadStepsModal);
 * `'list'` means the label cell holds a JSON/Python list rather than a
 * separator-joined string.
 */

export const LABELS_DELIMITER_OPTIONS = [
    { value: ';', label: 'Semicolon ;' },
    { value: ',', label: 'Comma ,' },
    { value: ' ', label: 'Space ␣' },
    { value: '|', label: 'Pipe |' },
    { value: '\t', label: 'Tab \\t' },
    { value: 'list', label: 'List []' },
];

/** Display label for a delimiter value, or undefined when unset/unknown. */
export function labelsDelimiterOptionLabel(value) {
    return LABELS_DELIMITER_OPTIONS.find((option) => option.value === value)?.label;
}

// Space is deliberately not auto-detected: a multi-word label ("New York")
// is indistinguishable from a space-separated label list.
const DETECTABLE_SEPARATORS = [';', '|', '\t', ','];

function stripWrappingQuotes(value) {
    const trimmed = value.trim();
    if (trimmed.length < 2) return trimmed;
    const start = trimmed[0];
    const end = trimmed[trimmed.length - 1];
    if ((start === '"' && end === '"') || (start === "'" && end === "'")) {
        return trimmed.slice(1, -1).trim();
    }
    return trimmed;
}

function looksLikeListLiteral(value) {
    const text = stripWrappingQuotes(value);
    return text.startsWith('[') && text.endsWith(']');
}

/**
 * Guess the labels delimiter from raw label-column values sampled from the
 * first records of a file. Returns one of the LABELS_DELIMITER_OPTIONS
 * values, or null when the structure is ambiguous (e.g. every sampled cell
 * holds a single label) so the caller can leave the choice to the user.
 *
 * Native arrays and `[...]` literals (JSON or Python, including extra CSV
 * quotes) are `'list'` *before* separator sniffing: list cells often contain
 * commas (`['a', 'b']`), and `Array.prototype.toString` / preview helpers
 * join arrays with commas, which would otherwise look like a comma delimiter.
 *
 * Pass raw cell values — not preview-unwrapped strings — so JSON array
 * columns stay arrays and CSV Python lists keep their brackets.
 */
export function detectLabelsDelimiter(values) {
    const samples = [];
    for (const value of values || []) {
        if (value == null || value === '') continue;
        if (Array.isArray(value)) {
            samples.push({ list: true, nativeArray: true, text: null });
            continue;
        }
        const text = String(value).trim();
        if (!text) continue;
        samples.push({
            list: looksLikeListLiteral(text),
            nativeArray: false,
            text,
        });
    }
    if (!samples.length) return null;

    const listCount = samples.filter((sample) => sample.list).length;
    const nativeArrayCount = samples.filter((sample) => sample.nativeArray).length;
    // Any parsed array means the column is list-typed. Bracket literals win
    // when they are at least half the sample (a stray `[note]` in an otherwise
    // comma-separated column should not flip the default).
    if (nativeArrayCount > 0 || listCount * 2 >= samples.length) {
        return 'list';
    }

    const separatorCandidates = samples.filter((sample) => !sample.list && sample.text);
    for (const separator of DETECTABLE_SEPARATORS) {
        if (separatorCandidates.some((sample) => sample.text.includes(separator))) {
            return separator;
        }
    }
    return null;
}

/**
 * Guess the labels delimiter from the first records of `column` on the given
 * upload entries. Returns a LABELS_DELIMITER_OPTIONS value, or null when there
 * are no samples or the structure is ambiguous.
 */
export function guessLabelsDelimiterFromEntries(entries, column) {
    if (!column) return null;
    const sampleable = (entries || []).filter(uploadEntryHasPreviewSamples);
    if (!sampleable.length) return null;
    const values = sampleable.flatMap((entry) =>
        fieldValuesFromUploadEntry(entry, column, 20, { raw: true })
    );
    return detectLabelsDelimiter(values);
}
